import { convertConfigForAggregation } from '@/utils/date-range.utils.js';
import { parseSegmentParams } from '@/utils/segments';
import { getIdeaPath } from '@/voc/helpers/common.helper';
import { operators as o } from '@pendo/aggregations';
import isEmpty from 'lodash/isEmpty';

export const commonVocSelectFields = (type) => {
    return [
        'id',
        'account',
        'accountId',
        'appIds',
        'associatedItemIds',
        'attachmentIds',
        'bannerImageId',
        'createdAt',
        'createdBy',
        'createdById',
        'createdByType',
        'formData',
        'highlight',
        'kind',
        'labelIds',
        'lastUpdatedAt',
        'lastUpdatedBy',
        'linksData',
        'numOfSuggestions',
        'ownedByUser',
        'ownedByUserId',
        'productAreaIds',
        'status',
        'titleGeneratedByAI',
        'type',
        'updatedById',
        'updatedByType',
        'vocItemTitle',
        'vocItemDescription',
        'score',
        ...(type === 'idea'
            ? [
                'accountVoteCount',
                'effort',
                'impact',
                'jiraIssueIds',
                'promotedToRoadmapItem',
                'revenue',
                'voteCount',
                'workaroundObj',
                'portalIds',
                'portalCustomValues',
            ]
            : []),
        ...(type === 'feedback'
            ? [
                'apps',
                'associatedItemsExpanded',
                'isUnattributed',
                'labels',
                'npsScore',
                'onBehalfOf',
                'onBehalfOfId',
                'onBehalfOfType',
                'productAreas',
                'source',
                'sourceName',
                'sourceCategory',
                'sourceIdentifier',
                'voteStrength',
                'submittedFromUrl',
            ]
            : []),
    ];
};

export const commonForkFields = (type) => [
    'createdBy',
    'lastUpdatedBy',
    'ownedByUser',
    ...(type === 'feedback' ? ['associatedItemsExpanded', 'onBehalfOf'] : []),
];

export const COMMON_VOC_SELECT = (type = 'idea', additionalFields = []) =>
    o.select([...commonVocSelectFields(type), ...additionalFields]);

export const COMMON_VOC_SOURCE_FILTER = (sources) => {
    return o.filter(
        sources
            .map((source) => {
                let filterStr = `sourceCategory == "${source.sourceCategory}" && source == "${source.source}" `;

                if (source.source === 'PollResponse' || source.source === 'NPS') {
                    filterStr += `&& startsWith(sourceIdentifier, "${source.sourceIdentifier}__")`;
                } else if (source.source === 'InternalFeedbackForm') {
                    filterStr += '|| sourceCategory == "PendoUI" && source == "IdeaList"';
                } else if (source.sourceIdentifier && source.sourceIdentifier !== '') {
                    filterStr += `&& sourceIdentifier == "${source.sourceIdentifier}"`;
                }

                return `(${filterStr})`;
            })
            .join(' || '),
    );
};

export const COMMON_VOC_NPS_FILTER = (npsScores) => {
    const npsScoreFilter = npsScores.map((score) => `formDataContent[2] == "${score.id ?? score}"`).join(' || ');

    return o.filter(`source == "NPS" && (${npsScoreFilter})`);
};

export const COMMON_VOC_EVAL_ACCOUNT_NAME = (accountDisplayName, evalToName, ObjToCheck, fallbackAccountIDName) => {
    if (!accountDisplayName) {
        return [
            o.evaluate(
                `${evalToName}`,
                `if (isNull(${ObjToCheck}.agent.name) || ${ObjToCheck}.agent.name == "", ${fallbackAccountIDName}, ${ObjToCheck}.agent.name)`,
            ),
        ];
    }

    const formattedAccountDisplayName = accountDisplayName.toLowerCase();

    return [
        o.evaluate(
            `${evalToName}`,
            `if (isNull(${ObjToCheck}.${formattedAccountDisplayName}) || ${ObjToCheck}.${formattedAccountDisplayName} == "", if (isNull(${ObjToCheck}.agent.name) || ${ObjToCheck}.agent.name == "", ${fallbackAccountIDName}, ${ObjToCheck}.agent.name), ${ObjToCheck}.${formattedAccountDisplayName})`,
        ),
    ];
};

export const COMMON_VOC_EVAL_VISITORS = ({
    voteSegmentId,
    includeAnonymousArg = '',
    shouldParse = true,
    joinOn = 'id',
}) => {
    let segmentId = voteSegmentId;
    let includeAnonymous = includeAnonymousArg;

    if (shouldParse) {
        const parsedSegment = parseSegmentParams({ segmentId: voteSegmentId });
        segmentId = parsedSegment.segmentId;
        includeAnonymous = voteSegmentId === 'everyone' ? null : parsedSegment.includeAnonymous;
    }

    return [
        ...(segmentId ? [o.segment(segmentId)] : []),
        // if filtering on identified visitors only, we need to also include votes with no visitorId
        // i.e. when a vote is added with only an account and no visitor
        ...(includeAnonymous === 'visitorId'
            ? [
                o.fork(o.pipeline(o.identified(includeAnonymous)), o.pipeline(o.filter('onBehalfOfId == ""'))),
                o.join(`${joinOn}`),
            ]
            : [...(includeAnonymous ? [o.identified(includeAnonymous)] : [])]),
    ];
};

export const COMMON_USERS_FOR_VOCITEMS_ONBEHALF_OF = [
    o.pipeline(
        o.filter('onBehalfOfType == "User"'),
        o.expand('onBehalfOfObj', 'user', 'onBehalfOfId'),
        o.evaluate('onBehalfOf.id', 'onBehalfOfId'),
        o.evaluate('onBehalfOf.type', '"User"'),
        o.evaluate('onBehalfOf.username', 'onBehalfOfObj.username'),
    ),
    o.pipeline(
        o.filter('onBehalfOfType == "Visitor"'),
        o.bulkExpand('onBehalfOfObj', 'visitor', 'onBehalfOfId'),
        o.evaluate('onBehalfOf.id', 'onBehalfOfId'),
        o.evaluate('onBehalfOf.type', '"Visitor"'),
        o.evaluate(
            'onBehalfOf.username',
            'if (isNull(onBehalfOfObj.agent.email), onBehalfOfObj.auto.id, onBehalfOfObj.agent.email)',
        ),
    ),
    o.pipeline(
        o.filter('isNull(onBehalfOfType) || len(onBehalfOfType) == 0'),
        o.evaluate('onBehalfOf.id', 'onBehalfOfId'),
        o.evaluate('onBehalfOf.type', '"Unknown"'),
        o.evaluate('onBehalfOf.username', '"Unknown"'),
    ),
];

export const COMMON_VOC_LINKED_ITEMS_AGG_PIPELINE = ({ isCsv = false }) => {
    const pipe = o.pipeline(
        o.unwind('associatedItemIds'),
        o.evaluate({ associatedId: 'associatedItemIds' }),
        o.merge(
            ['associatedId'],
            o.mappings({
                associatedItem: 'associatedItem',
            }),
            o.pipeline([
                o.sources.vocItems({
                    appId: 'expandAppIds("*")',
                    type: 'idea',
                }),
                o.evaluate({ associatedId: 'id' }),
                ...(isCsv
                    ? [
                        o.evaluate({
                            associatedItem: `"["+formDataContent[0]+"] (${getIdeaPath()}?openItem="+associatedId+")"`,
                        }),
                    ]
                    : [
                        o.evaluate({
                            'associatedItem.id': 'id',
                            'associatedItem.formData': 'formData',
                        }),
                    ]),
                o.select({
                    associatedId: 'associatedId',
                    associatedItem: 'associatedItem',
                }),
            ]),
        ),
        o.filter('!isNull(associatedItem)'),
        o.group(['id'], o.groupField('associatedItemsExpanded', o.list('associatedItem'))),
        ...(isCsv ? [o.evaluate('associatedItemsExpanded', 'join(associatedItemsExpanded, ", ")')] : []),

    );

    return pipe;
};

export function commonFieldsWithExcludes (type, excludes) {
    return commonVocSelectFields(type).filter((field) => !excludes.includes(field));
}

const updatedByPipelines = [
    o.pipeline(
        o.filter('updatedByType == "User"'),
        o.expand('lastUpdatedByObj', 'user', 'updatedById'),
        o.evaluate('lastUpdatedBy.id', 'updatedById'),
        o.evaluate('lastUpdatedBy.type', '"User"'),
        o.evaluate('lastUpdatedBy.username', 'lastUpdatedByObj.username'),
    ),
    o.pipeline(
        o.filter('updatedByType == "Visitor"'),
        o.bulkExpand('lastUpdatedByObj', 'visitor', 'updatedById'),
        o.evaluate('lastUpdatedBy.id', 'updatedById'),
        o.evaluate('lastUpdatedBy.type', '"Visitor"'),
        o.evaluate(
            'lastUpdatedBy.username',
            'if (isNull(lastUpdatedByObj.agent.email), lastUpdatedByObj.auto.id, lastUpdatedByObj.agent.email)',
        ),
    ),
    o.pipeline(
        o.filter('updatedByType == "IntegrationKey"'),
        o.expand('lastUpdatedByObj', 'integrationKey', 'updatedById'),
        o.evaluate('lastUpdatedBy.id', 'updatedById'),
        o.evaluate('lastUpdatedBy.type', '"IntegrationKey"'),
        o.evaluate('lastUpdatedBy.username', 'lastUpdatedByObj.username'),
    ),
];

export const COMMON_USERS_FOR_VOCITEMS_CREATED_BY = [
    o.pipeline(
        o.filter('createdByType == "User"'),
        o.expand('createdByObj', 'user', 'createdById'),
        o.evaluate('createdBy.id', 'createdById'),
        o.evaluate('createdBy.type', '"User"'),
        o.evaluate('createdBy.username', 'createdByObj.username'),
    ),
    o.pipeline(
        o.filter('createdByType == "Visitor"'),
        o.bulkExpand('createdByObj', 'visitor', 'createdById'),
        o.evaluate('createdBy.id', 'createdById'),
        o.evaluate('createdBy.type', '"Visitor"'),
        o.evaluate(
            'createdBy.username',
            'if (isNull(createdByObj.agent.email), createdByObj.auto.id, createdByObj.agent.email)',
        ),
    ),
    o.pipeline(
        o.filter('createdByType == "IntegrationKey"'),
        o.expand('createdByObj', 'integrationKey', 'createdById'),
        o.evaluate('createdBy.id', 'createdById'),
        o.evaluate('createdBy.type', '"IntegrationKey"'),
        o.evaluate('createdBy.username', 'createdByObj.username'),
    ),
];

const ownedByUserPipeline = [
    o.pipeline(
        o.expand('ownedByUserObj', 'user', 'ownedByUserId'),
        o.evaluate('ownedByUser.id', 'ownedByUserId'),
        o.evaluate('ownedByUser.username', 'ownedByUserObj.username'),
    ),
];

const workaroundPipeline = [
    o.pipeline(
        o.filter('!isNull(workaround)'),
        o.evaluate('workaroundCreatedByUserId', 'workaround.createdByUserId'),
        o.evaluate('workaroundLastUpdatedByUserId', 'workaround.lastUpdatedByUserId'),
        o.expand('workaroundCreatedByObj', 'user', 'workaroundCreatedByUserId'),
        o.expand('workaroundLastUpdatedByObj', 'user', 'workaroundLastUpdatedByUserId'),
        o.expand('workaroundNotificationLastSentByObj', 'user', 'workaroundNotificationLastSentByUserId'),
        o.evaluate('workaroundObj.createdByUser', 'workaroundCreatedByObj'),
        o.evaluate('workaroundObj.lastUpdatedByUser', 'workaroundLastUpdatedByObj'),
        o.evaluate('workaroundObj.lastSentByUser', 'workaroundNotificationLastSentByObj'),
        o.evaluate('workaroundObj.createdAt', 'workaround.createdAt'),
        o.evaluate('workaroundObj.lastUpdatedAt', 'workaround.lastUpdatedAt'),
        o.evaluate('workaroundObj.lastSentAt', 'workaroundNotificationLastSentAt'),
        o.evaluate('workaroundObj.body', 'workaround.body'),
    ),
];

export const INCLUDE_USERS_FOR_VOCITEMS = ({ sortBy } = {}) => {
    return [
        ...(sortBy?.endsWith('lastUpdatedBy.username') ? [] : updatedByPipelines),
        ...(sortBy?.endsWith('createdBy.username') ? [] : COMMON_USERS_FOR_VOCITEMS_CREATED_BY),
        ...(sortBy?.endsWith('ownedByUser.username') ? [] : ownedByUserPipeline),
        ...(sortBy?.endsWith('onBehalfOf.username') ? [] : COMMON_USERS_FOR_VOCITEMS_ONBEHALF_OF),
        ...workaroundPipeline,
    ];
};

export const COMMON_VOC_SOURCE_NAME = [
    o.evaluate(
        'sourceName',
        'if (len(split(source, "")) == 36 && len(split(source, "-")) == 5, sourceCategory, source)',

    ),
];

export function basicPipeline ({
    type = 'idea',
    appId = 'expandAppIds("*")',
    voteSegmentId = null,
    includeAnonymous = undefined,
    segmentId,
    accountTypes = [],
    alert = [],
    allTime = false,
    timeSeries = {},
    shouldFilterVotes = true,
    revenueField = null,
    accountDisplayName = null,
    groups = [],
    statuses = null,
    feedbackStatuses = [],
    createdBy = [],
    importance = [],
    labels = [],
    linkedIdeas = [],
    workarounds = [],
    createdAt = null,
    searchText = '',
    sources = [],
    account = null,
    npsScores = [],
    vocInsightIds = [],
    feedbackType = [],
    competitorNames = [],
    selectFields = null,
    includeSuggestions = false,
    assignee = [],
    expandInsights = false,
    accountTypeField = '',
    sortBy = '-lastUpdatedAt',
    isCsv = false,
    limit = 0,
    portals = [],
    accessibleGroupIds = [],
    accessibleAppIds = [],
    unattributedOnly = false,
}) {
    voteSegmentId ??= segmentId;
    statuses ??= feedbackStatuses;
    selectFields ??= commonVocSelectFields(type);
    const agg = {
        pipeline: [],
        fork: [],
        postFork: [],
    };

    if (includeSuggestions) {
        const idField = type === 'idea' ? 'leftId' : 'rightId';

        agg.pipeline.push(
            o.merge(
                ['id'],
                o.mappings({
                    id: 'id',
                    numOfSuggestions: 'numOfSuggestions',
                }),
                o.pipeline(
                    o.sources.suggestions({
                        state: 'pending',
                        type: 'ideaAndFeedbackItem',
                    }),
                    o.group([idField], o.groupField('numOfSuggestions', o.count(null))),
                    o.evaluate({
                        id: idField,
                        numOfSuggestions: 'if (numOfSuggestions > 10, 10, numOfSuggestions)',
                    }),
                    o.select(['id', 'numOfSuggestions']),
                ),
            ),
        );
    }

    if (searchText !== '') {
        agg.pipeline.push(
            o.merge(
                ['id'],
                o.mappings({
                    id: 'id',
                    esFound: 'esFound',
                    highlight: 'highlight',
                    score: 'score',
                }),
                o.pipeline(
                    o.sources.elasticsearch({
                        index: 'vocitems',
                        input: searchText,
                        type,
                        config: {
                            includeHighlights: true,
                            includeScore: true,
                            querySettings: { exactMatch: true },
                        },
                    }),
                ),
            ),
            o.filter('esFound == true'),
        );
    }

    if (npsScores?.length > 0) {
        agg.pipeline.push(COMMON_VOC_NPS_FILTER(npsScores));
    }

    let votesSource = o.sources.votes();

    if (!allTime) {
        votesSource = o.sources.votesTimeSeries(o.timeSeries(timeSeries));
    }

    const votesAgg = [
        o.pipeline(o.group(['vocItemId'], o.groupField('votesCount', o.count(null)))),
        o.pipeline(
            o.filter('accountId != ""'),
            o.group(['vocItemId'], o.groupField('accountVoteCount', o.count('accountId'))),
        ),
    ];

    if (createdAt) {
        agg.pipeline.push(o.pipeline(o.filter(`createdAt ${createdAt.expression || '=='} ${createdAt.value}`)));
    }

    if (typeof revenueField === 'string' && type === 'idea') {
        votesAgg.push(
            o.pipeline(
                o.filter('accountId != ""'),
                o.filter('strength > 0'),
                o.group(['vocItemId', 'accountId']),
                o.bulkExpand('account', 'account', 'accountId'),
                o.filter(`!isNull(${revenueField.toLowerCase()})`),
                o.group(['vocItemId'], o.groupField('revenue', o.sum(revenueField.toLowerCase()))),
            ),
        );
    }

    let importanceFilter;
    if (importance?.length > 0) {
        importanceFilter = o.filter(
            importance
                .map((currentImportance) => {
                    const id = currentImportance?.id ? currentImportance.id : currentImportance;

                    return `strength == ${id}`;
                })
                .join(' || '),
        );
    }

    if (portals?.length > 0) {
        const portalFilters = [];
        const hasAny = portals.includes('*');
        const hasNone = portals.includes('');

        if (hasAny) {
            portalFilters.push('len(portalIds) > 0');
        }
        if (hasNone) {
            portalFilters.push('len(portalIds) == 0');
        }
        portals.forEach((currentPortal) => {
            const id = currentPortal?.id === undefined ? currentPortal : currentPortal.id;
            if (id !== '' && id !== '*') {
                portalFilters.push(`contains(portalIds, "${id}")`);
            }
        });
        if (portalFilters.length > 0) {
            agg.pipeline.push(
                o.filter(portalFilters.join(' || ')),
            );
        }
    }
    const accountId = account?.id ?? account;
    const accountFilter = accountId ? o.filter(`accountId == "${accountId}"`) : undefined;

    if (sources?.length > 0) {
        agg.pipeline.push(COMMON_VOC_SOURCE_FILTER(sources));
    }

    if (selectFields.includes('productAreaIds') || groups?.length > 0) {
        agg.pipeline.push(o.evaluate('productAreaIds', 'productAreaVocIds'));

        if (accessibleGroupIds?.length > 0) {
            agg.pipeline.push(o.evaluate('accessibleGroupIds', JSON.stringify(accessibleGroupIds)));
            agg.pipeline.push(
                o.evaluate(
                    'productAreaIds',
                    'filter(paId, productAreaIds, contains(accessibleGroupIds, paId))',
                ),
            );
        }
    }
    if (groups?.length > 0) {
        agg.pipeline.push(
            o.filter(
                groups
                    .map((group) => {
                        const id = group?.id === undefined ? group : group.id;
                        if (id === '') {
                            return 'len(productAreaIds) == 0';
                        }

                        return `contains(productAreaIds, "${id}")`;
                    })
                    .join(' || '),
            ),
        );
    }

    if (vocInsightIds?.length) {
        agg.pipeline.push(
            o.filter(
                vocInsightIds
                    .map((vocInsightId) => {
                        return `contains(vocInsightIds, "${vocInsightId}")`;
                    })
                    .join(' || '),
            ),
        );
    }

    if (statuses?.length > 0) {
        agg.pipeline.push(
            o.filter(
                statuses
                    .map((currentStatus) => {
                        const id = currentStatus?.id === undefined ? currentStatus : currentStatus.id;

                        return `statusId == "${id}"`;
                    })
                    .join(' || '),
            ),
        );
    }

    if (createdBy?.length > 0) {
        agg.pipeline.push(
            o.filter(
                createdBy
                    .map((currentCreatedBy) => {
                        const id = currentCreatedBy?.id === undefined ? currentCreatedBy : currentCreatedBy.id;

                        return `createdById == "${id}"`;
                    })
                    .join(' || '),
            ),
        );
    }

    if (assignee?.length > 0) {
        agg.pipeline.push(
            o.filter(
                assignee
                    .map((currentAssignee) => {
                        const id = currentAssignee?.id === undefined ? currentAssignee : currentAssignee.id;

                        return `ownedByUserId == "${id}"`;
                    })
                    .join(' || '),
            ),
        );
    }

    if (labels?.length > 0) {
        agg.pipeline.push(
            o.filter(
                labels
                    .map((currentLabels) => {
                        const id = currentLabels?.id === undefined ? currentLabels : currentLabels.id;

                        return `contains(labelIds, "${id}")`;
                    })
                    .join(' || '),
            ),
        );
    }

    if (linkedIdeas?.length > 0 && linkedIdeas?.length < 2) {
        agg.pipeline.push(
            o.filter(
                linkedIdeas[0] === true || linkedIdeas[0]?.id === true
                    ? 'len(associatedItemIds) > 0'
                    : 'len(associatedItemIds) == 0',
            ),
        );
    }

    if (workarounds?.length === 1) {
        agg.pipeline.push(
            o.filter(
                workarounds[0] === true || workarounds[0]?.id === true ? '!isNull(workaround)' : 'isNull(workaround)',
            ),
        );
    }

    if (unattributedOnly && type === 'feedback') {
        agg.pipeline.push(o.filter('isUnattributed == true || onBehalfOfId == "" || isNull(onBehalfOfId)'));
    }

    if (type === 'idea') {
        agg.pipeline.push(
            o.merge(
                ['id'],
                o.mappings({
                    voteCount: 'votesCount',
                    accountVoteCount: 'accountVoteCount',
                    revenue: 'revenue',
                }),
                o.pipeline([
                    votesSource,
                    ...(importanceFilter ? [importanceFilter] : []),
                    ...(accountFilter ? [accountFilter] : []),
                    ...(accessibleAppIds && accessibleAppIds.length > 0 ? [
                        o.merge(
                            ['sourceIdentifier'],
                            o.mappings({
                                feedbackItemAppIds: 'appIds',
                            }),
                            o.pipeline(
                                o.sources.vocItems({
                                    type: 'feedback',
                                    appId: accessibleAppIds,
                                }),
                                o.select({
                                    sourceIdentifier: 'id',
                                    appIds: 'appIds',
                                }),
                            ),
                        ),
                        o.filter('(feedbackItemAppIds != null && source == "Feedback") || source != "Feedback"'),
                    ] : []),
                    ...(includeAnonymous === undefined
                        ? [...COMMON_VOC_EVAL_VISITORS({ voteSegmentId })]
                        : [
                            ...COMMON_VOC_EVAL_VISITORS({
                                voteSegmentId,
                                includeAnonymousArg: includeAnonymous,
                                shouldParse: false,
                            }),
                        ]),
                    o.fork(...votesAgg),
                    o.join('vocItemId'),
                    o.select({
                        id: 'vocItemId',
                        votesCount: 'votesCount',
                        accountVoteCount: 'accountVoteCount',
                        revenue: 'revenue',
                    }),
                ]),
            ),
        );
    } else if (
        type === 'feedback' &&
        (voteSegmentId ||
            !allTime ||
            importance?.length ||
            accountId ||
            selectFields.some((field) =>
                ['accountVoteCount', 'effort', 'impact', 'voteCount', 'voteStrength'].includes(field),
            ))
    ) {
        agg.pipeline.push(
            o.merge(
                ['id'],
                o.mappings({
                    voteStrength: 'strength',
                    voteCount: '1',
                }),
                o.pipeline([
                    votesSource,
                    ...(importanceFilter ? [importanceFilter] : []),
                    ...(accountFilter ? [accountFilter] : []),
                    o.segment(voteSegmentId),
                    o.select({
                        id: 'vocItemId',
                        voteId: 'id',
                        strength: 'strength',
                    }),
                ]),
            ),
        );
    }

    if (
        ((voteSegmentId || !allTime || importance?.length || accountId) && shouldFilterVotes) ||
        selectFields.some((field) => ['accountVoteCount', 'effort', 'impact', 'voteCount'].includes(field))
    ) {
        agg.pipeline.push(
            o.evaluate({
                voteCount: 'if (isNull(voteCount), 0, voteCount)',
                accountVoteCount: 'if (isNull(accountVoteCount), 0, accountVoteCount)',
            }),
        );
    }

    if (voteSegmentId && includeAnonymous === undefined) {
        const segment = parseSegmentParams({ segmentId: voteSegmentId });
        voteSegmentId = segment.segmentId;
    }

    if ((voteSegmentId || !allTime || importance?.length || accountId) && shouldFilterVotes) {
        agg.pipeline.push(o.filter('voteCount > 0'));
    }
    // map() on formData does not work, so using formDataTitle and formDataContent instead
    if (isCsv) {
        agg.pipeline.push(o.evaluate('formDataQuestionPairing', 'map(i, x, formDataTitle, "\'" + x + "\' \'" + formDataContent[i] + "\'")[1:len(formDataTitle)]'));
    }
    const evals = {
        ...(revenueField && type === 'idea' && { revenue: 'if (isNull(revenue), 0, round(revenue * 100) / 100)' }),
        ...(selectFields.includes('kind') && { kind: '"vocItem"' }),
        ...(selectFields.includes('status') && { 'status.id': 'statusId' }),
        ...(selectFields.includes('vocItemTitle') && { vocItemTitle: 'formDataContent[0]' }),
        ...(selectFields.includes('vocItemDescription') && { ...(isCsv ? { vocItemDescription: 'join(formDataQuestionPairing, " - ")' }: { vocItemDescription: 'formDataContent[1]' }) }),
        ...(type === 'feedback' &&
            selectFields.includes('npsScore') && { npsScore: 'if (source == "NPS", formDataContent[2], null)' }),
    };

    if (!isEmpty(evals)) {
        agg.pipeline.push(o.evaluate(evals));
    }

    if (sortBy?.endsWith('status.name')) {
        agg.pipeline.push(o.expand('statusObj', 'status', 'status.id'));
        agg.pipeline.push(o.evaluate('status.name', 'statusObj.name'));
    } else if (!sortBy?.endsWith('status.name') && isCsv) {
        agg.fork.push(o.pipeline(o.expand('statusObj', 'status', 'status.id')));
        agg.postFork.push(o.evaluate('status.name', 'statusObj.name'));
    }

    if (sortBy?.endsWith('productAreas')) {
        agg.pipeline.push(o.expandList('productAreasObj', 'productArea', 'productAreaIds'));
        agg.pipeline.push(
            o.evaluate(
                'productAreas',
                'if (isList(productAreasObj) == false, null, join(sortUnique(map(filter(productArea, productAreasObj, !isNull(productArea)), name)), ", "))',
            ),
        );
    } else if (!sortBy?.endsWith('productAreas') && isCsv) {
        agg.fork.push(o.pipeline(o.expandList('productAreasObj', 'productArea', 'productAreaIds')));
        agg.postFork.push(
            o.evaluate(
                'productAreas',
                'if (isList(productAreasObj) == false, null, join(sortUnique(map(filter(productArea, productAreasObj, !isNull(productArea)), name)), ", "))',
            ),
        );
    }

    if (sortBy?.endsWith('apps')) {
        agg.pipeline.push(o.expandList('appsObj', 'app', 'appIds'));
        agg.pipeline.push(
            o.evaluate(
                'apps',
                'if (isList(appsObj) == false, null, join(sortUnique(map(filter(app, appsObj, !isNull(app)), name)), ", "))',
            ),
        );
    } else if (!sortBy?.endsWith('apps') && isCsv) {
        agg.fork.push(o.pipeline(o.expandList('appsObj', 'app', 'appIds')));
        agg.postFork.push(
            o.evaluate(
                'apps',
                'if (isList(appsObj) == false, null, join(sortUnique(map(filter(app, appsObj, !isNull(app)), name)), ", "))',
            ),
        );
    }

    if (sortBy?.endsWith('labels')) {
        agg.pipeline.push(o.expandList('labelsObj', 'label', 'labelIds'));
        agg.pipeline.push(
            o.evaluate(
                'labels',
                'if (isList(labelsObj) == false, null, join(sortUnique(map(filter(label, labelsObj, !isNull(label)), category.name + if(category.name != "", ": ", "") + name)), ", "))',
            ),
        );
    } else if (!sortBy?.endsWith('labels') && isCsv) {
        agg.fork.push(o.pipeline(o.expandList('labelsObj', 'label', 'labelIds')));
        agg.postFork.push(
            o.evaluate(
                'labels',
                'if (isList(labelsObj) == false, null, join(sortUnique(map(filter(label, labelsObj, !isNull(label)), category.name + if(category.name != "", ": ", "") + name)), ", "))',
            ),
        );
    }

    if (sortBy?.endsWith('createdBy.username')) {
        agg.pipeline.push(o.fork(...COMMON_USERS_FOR_VOCITEMS_CREATED_BY), o.join('id'));
    }

    if (sortBy?.endsWith('ownedByUser.username')) {
        agg.pipeline.push(...ownedByUserPipeline);
    }

    if (sortBy?.endsWith('lastUpdatedBy.username')) {
        agg.pipeline.push(o.fork(...updatedByPipelines), o.join('id'));
    }

    if (sortBy?.endsWith('onBehalfOf.username')) {
        agg.pipeline.push(o.fork(...COMMON_USERS_FOR_VOCITEMS_ONBEHALF_OF), o.join('id'));
    }

    if (
        type === 'feedback' &&
        (selectFields.includes('account') || accountTypes?.length > 0 || sortBy?.endsWith('account.name'))
    ) {
        const revenueFieldSelector = revenueField ? revenueField.replace('account', 'accountObj') : null;
        if (sortBy?.endsWith('account.name') || accountTypes?.length > 0) {
            agg.pipeline.push(
                o.bulkExpand('accountObj', 'account', 'accountId'),
                o.evaluate('account.id', 'accountId'),
                ...(revenueField ? [o.evaluate('account.revenue', revenueFieldSelector)] : []),
                ...COMMON_VOC_EVAL_ACCOUNT_NAME(accountDisplayName, 'account.name', 'accountObj', 'account.id'),
            );
            if (accountTypes?.length > 0) {
                const accountTypeFilters = accountTypes.reduce((acc, type) => {
                    acc.push(type.values.map((value) => `(accountObj.${accountTypeField}=="${value}")`).join(' || '));

                    return acc;
                }, []);

                agg.pipeline.push(o.filter(accountTypeFilters.join(' || ')));
            }
        } else {
            agg.fork.push(
                o.pipeline([
                    o.bulkExpand('accountObj', 'account', 'accountId'),
                    o.evaluate('account.id', 'accountId'),
                    ...(revenueField ? [o.evaluate('account.revenue', revenueFieldSelector)] : []),
                    ...COMMON_VOC_EVAL_ACCOUNT_NAME(accountDisplayName, 'account.name', 'accountObj', 'account.id'),
                ]),
            );
        }
    }

    if (type === 'feedback' && selectFields.includes('sourceName')) {
        agg.pipeline.push(
            ...COMMON_VOC_SOURCE_NAME,
        );
    }

    if (alert.length || feedbackType.length || competitorNames.length || expandInsights) {
        const commonSelectFields = selectFields.filter(
            (field) => field !== 'account' && !commonForkFields(type).includes(field),
        );

        if (limit > 0 && limit <= 1000 && !(alert.length || feedbackType.length || competitorNames.length)) {
            agg.fork.push(o.pipeline(o.expandList('vocInsights', 'vocInsight', 'vocInsightIds')));
        } else {
            const insightsPipeline = [
                o.select([...commonSelectFields, 'vocInsightIds']),
                o.unwind('vocInsightIds', { keepEmpty: true }),
                o.merge(
                    ['vocInsightIds'],
                    {},
                    o.pipeline(
                        o.sources.vocInsights({
                            disableDatastoreIterator: true,
                        }),
                        ...expandInsights ? [
                            ...(feedbackType.length ? [o.evaluate('feedbackType', 'feedbackType')] : []),
                            ...(alert.length ? [o.evaluate('alert', 'alert')] : []),
                            ...(competitorNames.length ? [o.evaluate('competitorName', 'competitorName')] : []),
                        ] : [
                            ...(feedbackType.length ? [o.filter(feedbackType.map((type) => `feedbackType == "${type}"`).join(' || '))] : []),
                            ...(alert.length ? [o.filter(alert.map((type) => `alert == "${type}"`).join(' || '))] : []),
                            ...(competitorNames.length ? [o.filter(competitorNames.map((name) => `competitorName == "${name}"`).join(' || '))] : []),
                        ],
                        o.evaluate('vocInsight.id', 'id'),
                        ...(expandInsights
                            ? [
                                o.evaluate({
                                    'vocInsight.feedbackType': 'feedbackType',
                                    'vocInsight.quotes': 'quoteText',
                                }),
                            ]
                            : []),
                        o.select({
                            vocInsightIds: 'id',
                            vocInsight: 'vocInsight',
                            alert: 'alert',
                            feedbackType: 'feedbackType',
                            competitorName: 'competitorName',
                            quotes: 'quotes',
                        }),
                    ),
                ),
            ];

            if (!expandInsights && (alert.length || feedbackType.length || competitorNames.length)) {
                insightsPipeline.push(o.filter('!isNull(vocInsight.id)'));
            }
            if (expandInsights) {
                insightsPipeline.push(
                    o.group(
                        ['id'],
                        o.groupField('vocInsights', o.list('vocInsight')),
                        o.groupField('alerts', o.listIf('alert', '!isNull(alert) && alert != ""')),
                        o.groupField('feedbackTypes', o.listIf('feedbackType', '!isNull(feedbackType) && feedbackType != ""')),
                        o.groupField('competitorNames', o.listIf('competitorName', '!isNull(competitorName) && competitorName != ""')),
                        ...commonSelectFields.map((field) => o.groupField(field, o.first(field))),
                    ),
                );
                if (alert.length) {
                    insightsPipeline.push(o.filter(alert.map((type) => `contains(alerts,"${type}")`).join(' || ')));
                }
                if (feedbackType.length) {
                    insightsPipeline.push(
                        o.filter(feedbackType.map((type) => `contains(feedbackTypes,"${type}")`).join(' || ')),
                    );
                }
                if (competitorNames.length) {
                    insightsPipeline.push(
                        o.filter(competitorNames.map((name) => `contains(competitorNames,"${name}")`).join(' || ')),
                    );
                }
            }

            if (alert.length || feedbackType.length || competitorNames.length) {
                agg.pipeline.push(...insightsPipeline);
            } else {
                agg.fork.push(o.pipeline(...insightsPipeline));
            }
        }
    }

    agg.source = o.sources.vocItems({ appId, type });

    if (expandInsights && !selectFields.includes('vocInsights')) {
        selectFields.push('vocInsights');
    }

    agg.select = o.select(selectFields);

    return agg;
}

export function processVocItemFilters (filters = {}) {
    let {
        account,
        accountDisplayName,
        accountTypes = [],
        alert = [],
        allTime,
        appId = 'expandAppIds("*")',
        assignee = [],
        createdBy = [],
        feedbackType = [],
        competitorNames = [],
        feedbackStatuses = [],
        groups = [],
        importance = [],
        labels = [],
        linkedIdeas = [],
        npsScores = [],
        portals = [],
        searchText = '',
        segmentId,
        sources = [],
        statuses,
        timeSeries,
        timeSeriesConfig,
        vocInsightIds = [],
        voteSegmentId,
        workarounds = [],
        unattributedOnly = false,
    } = filters;

    timeSeriesConfig ??= timeSeries ? convertConfigForAggregation(timeSeries?.config || timeSeries) : null;
    allTime = allTime || !timeSeriesConfig;
    voteSegmentId ??= segmentId;
    statuses ??= feedbackStatuses;

    if (voteSegmentId === 'everyone') voteSegmentId = undefined;

    return {
        account,
        accountDisplayName,
        accountTypes,
        alert,
        allTime,
        appId,
        assignee,
        createdBy,
        feedbackType,
        competitorNames,
        groups,
        importance,
        labels,
        linkedIdeas,
        npsScores,
        portals,
        searchText,
        sources,
        statuses,
        timeSeries: timeSeriesConfig,
        vocInsightIds,
        voteSegmentId,
        workarounds,
        unattributedOnly,
    };
}

export const COMMON_VOC_VOTE_AGG_PIPELINE = (
    vocItemId,
    voteSegmentId,
    allTime,
    timeSeries,
    voteFilter,
    importanceStrength,
    selectedAccount,
    accessibleAppIds,
) => {
    let votesSource = o.sources.votes({ vocItemId });

    if (!allTime) {
        votesSource = o.sources.votesTimeSeries({
            timeSeries,
            vocItemId,
        });
    }

    const pipe = o.pipeline(votesSource, COMMON_VOC_EVAL_VISITORS({ voteSegmentId }));

    if (importanceStrength?.length > 0) {
        pipe.push(
            o.filter(
                importanceStrength
                    .map((currentImportance) => {
                        return `strength == ${currentImportance.id}`;
                    })
                    .join(' || '),
            ),
        );
    } else {
        pipe.push(o.filter('strength >= -1'));
    }

    if (voteFilter?.strength) {
        pipe.push(o.filter(`strength == ${voteFilter.strength}`));
    } else {
        pipe.push(o.filter('strength >= -1'));
    }

    if (voteFilter?.guideId) {
        pipe.push(o.filter(`source == "IdeaPoll" && sourceIdentifier == "${voteFilter.guideId}"`));
    }

    if (selectedAccount?.id) {
        pipe.push(o.accountFilter(selectedAccount));
    }

    if (accessibleAppIds && accessibleAppIds.length > 0) {
        pipe.push(
            o.merge(
                ['sourceIdentifier'],
                o.mappings({
                    feedbackItemAppIds: 'appIds',
                }),
                o.pipeline(
                    o.sources.vocItems({
                        type: 'feedback',
                        appId: accessibleAppIds,
                    }),
                    o.select({
                        sourceIdentifier: 'id',
                        appIds: 'appIds',
                    }),
                ),
            ),
            o.filter('(feedbackItemAppIds != null && source == "Feedback") || source != "Feedback"'),
        );

    }

    return pipe;
};
